import { Ellipsis } from "lucide-react";
import { motion } from "motion/react";
import { type ChangeEventHandler, memo, type Ref } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface TemporaryCard {
  tempId: string;
  question: string;
  answer: string;
}

export interface GeneratedCardProps extends TemporaryCard {
  updateQuestion: (tempId: string, newQuestion: string) => void;
  updateAnswer: (tempId: string, newAnswer: string) => void;

  deleteCard: (tempId: string) => void;
  ref?: Ref<HTMLDivElement>; // This is necessary to get the AnimatePresence 'popLayout' mode working correctly - see: https://motion.dev/docs/react-animate-presence#mode
}

export const GeneratedCard = memo(
  ({
    tempId,
    question,
    answer,
    updateQuestion,
    updateAnswer,
    deleteCard,
    ref,
  }: GeneratedCardProps) => {
    const handleQuestionChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
      updateQuestion(tempId, event.target.value);
    };

    const handleAnswerChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
      updateAnswer(tempId, event.target.value);
    };

    const handleDeleteClick = () => deleteCard(tempId);

    return (
      <motion.div
        ref={ref}
        className="w-64 h-56 p-4 border rounded shadow-sm flex flex-col justify-between bg-white hover:shadow-md transition-shadow relative"
        layout
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{
          type: "tween",
        }}
      >
        <div className="font-medium mb-2 flex-1 overflow-hidden relative">
          <textarea
            value={question}
            onChange={handleQuestionChange}
            className="w-full h-full resize-none outline-none font-medium"
            style={{ overflow: "auto" }}
          />
          <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
        </div>
        <>
          <hr className="border-gray-200 my-2 w-full" />
          <div className="text-gray-600 flex-1 overflow-hidden relative">
            <textarea
              value={answer}
              onChange={handleAnswerChange}
              className="w-full h-full resize-none outline-none text-gray-600"
              style={{ overflow: "auto" }}
            />

            <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
          </div>
        </>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon-md" variant="ghost" className="absolute bottom-2 right-2">
              <Ellipsis />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-32">
            <DropdownMenuItem variant="destructive" onClick={handleDeleteClick}>
              Delete card
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </motion.div>
    );
  }
);
